/**
 * Guides index — METADATA-ONLY as of 2026-09-29 (pass 92u).
 *
 * Previously this module eager-imported all ~800 guide modules (every
 * Body JSX component), which pulled the entire content library into the
 * main JS bundle (1.43MB gzipped) because GuidePage + CalculatorPage are
 * eager routes in App.js. RUM showed FCP/LCP stuck in "needs
 * improvement" largely because of that download + parse cost.
 *
 * Now: `GUIDES` comes from `_generated/guide-meta.json` (regenerate with
 * `node scripts/generate-guide-meta.js` — wired into prestart/prebuild).
 * GuidePage lazy-loads only the visited guide's module via
 * `import(`@/guides/${slug}.jsx`)`, which webpack code-splits into
 * per-guide chunks fetched on demand.
 *
 * Export contract is unchanged — every helper below operates purely on
 * metadata fields (slug, title, category, date, related, relatedStates,
 * applicableEverywhere), so all existing consumers work unmodified.
 */
import GUIDE_META from "./_generated/guide-meta.json";

export const GUIDES = GUIDE_META;

export function getGuide(slug) {
  return GUIDES.find((g) => g.slug === slug);
}

export function getRelatedGuides(currentSlug, limit = 3) {
  return GUIDES.filter((g) => g.slug !== currentSlug).slice(0, limit);
}

/**
 * Topic groups for the /guides index page (UI-only — does not change URLs).
 * Each guide is bucketed by its single `related` calculator; guides covering
 * multiple calculators land in "General".
 */
const TOPIC_LABELS = {
  roi: "ROI & Resale",
  bathroom: "Bathroom Remodel",
  kitchen: "Kitchen Remodel",
  flooring: "Flooring",
  roofing: "Roofing",
  deck: "Deck Construction",
  basement: "Basement Finishing",
  windows: "Window Replacement",
  solar: "Solar Panels",
  fence: "Fence Installation",
  pool: "Pool Installation",
  painting: "Painting",
  adu: "ADU & Garage Conversion",
  financing: "Financing",
  outdoor: "Outdoor Living",
  homegym: "Home Gym",
  maintenance: "Home Maintenance",
  hardscape: "Hardscape & Patios",
  landscape: "Landscaping",
  disaster: "Storm & Disaster",
  hvac: "HVAC",
  furnace: "Heating & Furnace",
  insulation: "Insulation",
  smartHome: "Smart Home",
  general: "General",
};
const TOPIC_ORDER = [
  "roi", "bathroom", "kitchen", "flooring", "roofing", "deck", "basement",
  "windows", "solar", "fence", "pool", "painting",
  "hvac", "furnace", "insulation", "smartHome",
  "adu", "financing", "outdoor", "hardscape", "landscape", "homegym", "maintenance",
  "disaster",
  "general",
];

// Bucket a guide by its *primary* (first) related calculator that maps to a
// known topic label. The "ROI & Resale" bucket takes priority — any guide
// with `category: "ROI"` lands there regardless of its `related` array. We
// also honor a few explicit category names for the 2026-05 editorial
// clusters (ADU, Financing, Outdoor Living, Home Gym, Maintenance) so they
// surface in their own filter chips instead of being lumped into General.
// Multi-purpose evergreen guides (cross-listed across many calcs AND flagged
// applicableEverywhere=true) without one of those explicit categories fall
// to "General".
const CATEGORY_TO_TOPIC = {
  ROI: "roi",
  ADU: "adu",
  Financing: "financing",
  "Outdoor Living": "outdoor",
  "Home Gym": "homegym",
  Maintenance: "maintenance",
  Disaster: "disaster",
  HVAC: "hvac",
  Furnace: "furnace",
  Insulation: "insulation",
  "Smart Home": "smartHome",
  // "Hiring" routes to General — the Contractor-Proof Scope of Work guide has
  // `related: ["bathroom"]` (so it still surfaces on bathroom-calculator
  // "related guides" strips), but its topic bucket on /guides is "General".
  // Category takes precedence over related[] here, fixing the mis-grouping
  // without touching downstream related-guides logic.
  Hiring: "general",
};

export function getGuideTopic(guide) {
  const fromCategory = CATEGORY_TO_TOPIC[guide.category];
  if (fromCategory) return fromCategory;
  const rel = guide.related || [];
  if (guide.applicableEverywhere === true && rel.length > 1) return "general";
  for (const r of rel) {
    if (TOPIC_LABELS[r]) return r;
  }
  return "general";
}

export function getGuidesGroupedByTopic() {
  const groups = Object.fromEntries(TOPIC_ORDER.map((t) => [t, []]));
  for (const g of GUIDES) groups[getGuideTopic(g)].push(g);
  // Pin the hub + Power Trio first inside the ROI bucket so readers hit the
  // canonical entry points (ranking + interactive calculator) before the
  // individual project deep-dives.
  const ROI_PRIORITY = ["best-roi-renovations-2026-ranked", "curb-appeal-power-trio-2026"];
  if (groups.roi && groups.roi.length > 0) {
    groups.roi.sort((a, b) => {
      const ai = ROI_PRIORITY.indexOf(a.slug);
      const bi = ROI_PRIORITY.indexOf(b.slug);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }
  return TOPIC_ORDER
    .filter((t) => groups[t].length > 0)
    .map((t) => ({ topic: t, label: TOPIC_LABELS[t], guides: groups[t] }));
}

/**
 * Return the canonical long-form guide for a given (calculator, state) pair,
 * if one exists. Used by the short-slug state landing pages (e.g.
 * `/painting-cost-iowa`) to point `<link rel="canonical">` at the richer
 * editorial guide (`/guides/painting-cost-iowa-2026`) and consolidate
 * ranking signals — addresses the P0 GSC duplicate-content / slug-collision
 * flag where both URLs were competing for the same query.
 *
 * Returns the guide's slug string (without the `/guides/` prefix) or null
 * when no single-state editorial deep-dive exists for the pair (in which
 * case the short URL self-canonicalizes).
 */
export function getCanonicalGuideForStatePage(calcSlug, stateCode) {
  const match = GUIDES.find((g) => {
    if (!(g.related || []).includes(calcSlug)) return false;
    if (g.applicableEverywhere === true) return false;
    if (!g.relatedStates || g.relatedStates.length !== 1) return false;
    return g.relatedStates[0] === stateCode;
  });
  return match ? match.slug : null;
}

export function getGuidesForStatePage(calcSlug, stateCode, limit = 3) {
  const candidates = GUIDES.filter((g) => {
    if (!(g.related || []).includes(calcSlug)) return false;
    const rs = g.relatedStates;
    if (!rs || rs.length === 0) return true; // no state filter → general
    if (rs.includes(stateCode)) return true; // exact state match
    return g.applicableEverywhere === true; // broadly-applicable fallback
  });
  return candidates
    .sort((a, b) => {
      const aExact = (a.relatedStates || []).includes(stateCode) ? 0 : 1;
      const bExact = (b.relatedStates || []).includes(stateCode) ? 0 : 1;
      return aExact - bExact;
    })
    .slice(0, limit);
}

/**
 * Return the curated, state-specific editorial guides for a calculator
 * (only guides that target a specific state via `relatedStates`). Used to
 * power the "Featured: state-specific cost guides" block on each calculator
 * page — surfaces the highest-quality long-form pages above-the-fold rather
 * than burying them at the bottom in the 50-state link list.
 *
 * Returned in a deterministic order: by `date` desc (newest first), then by
 * state code for stable rendering.
 */
export function getStateGuidesForCalculator(calcSlug, limit = 6) {
  const matches = GUIDES.filter((g) => {
    if (!(g.related || []).includes(calcSlug)) return false;
    // Must be a single-state editorial deep-dive — i.e. one entry in
    // `relatedStates` and NOT flagged as generally applicable.
    if (g.applicableEverywhere === true) return false;
    if (!g.relatedStates || g.relatedStates.length !== 1) return false;
    return true;
  });
  matches.sort((a, b) => {
    if (a.date && b.date && a.date !== b.date) return a.date < b.date ? 1 : -1;
    return (a.relatedStates?.[0] || "").localeCompare(b.relatedStates?.[0] || "");
  });
  return matches.slice(0, limit);
}

/**
 * Topical / non-state-specific guides for a calculator — used for the "More
 * reading" block on each calculator page (separate from the state-specific
 * `getStateGuidesForCalculator` block). Surfaces guides that:
 *   - have `calcSlug` in `related`
 *   - target multiple calcs OR have `applicableEverywhere === true` (i.e.
 *     they are NOT a single-state editorial — those are handled separately)
 * Sorted newest first by `date`.
 */
export function getTopicalGuidesForCalculator(calcSlug, limit = 4) {
  const matches = GUIDES.filter((g) => {
    if (!(g.related || []).includes(calcSlug)) return false;
    // Skip single-state deep-dives — already surfaced in the state block.
    if (g.relatedStates && g.relatedStates.length === 1 && !g.applicableEverywhere) return false;
    return true;
  });
  matches.sort((a, b) => {
    if (a.date && b.date && a.date !== b.date) return a.date < b.date ? 1 : -1;
    return a.title.localeCompare(b.title);
  });
  return matches.slice(0, limit);
}

/**
 * Sibling state guides — every OTHER state-specific guide for the same
 * state as the current guide. Used by GuidePage.jsx to boost inbound
 * internal-link count on every programmatic state page. Was averaging 1
 * inbound link per state guide (the neighbor block links out, but few
 * pages linked in) — Google flagged many as "Crawled - not indexed" as a
 * result. Cross-linking sibling projects within the same state typically
 * 5-6×s the inbound link count and gives crawlers a clear "this state has
 * a coordinated content cluster" signal.
 *
 * Returns: [{ slug, title, category, related }, ...] for the same single
 * relatedStates code as `guide`, excluding `guide` itself. Empty if the
 * current guide isn't a single-state page.
 */
export function getSameStateGuides(guide) {
  if (!guide || !Array.isArray(guide.relatedStates) || guide.relatedStates.length !== 1) {
    return [];
  }
  const stateCode = guide.relatedStates[0];
  return GUIDES.filter((g) => {
    if (g.slug === guide.slug) return false;
    if (!Array.isArray(g.relatedStates) || g.relatedStates.length !== 1) return false;
    return g.relatedStates[0] === stateCode;
  }).sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Group single-state editorial guides by state code. Used by the
 * Guides Index "Browse by state" panel to surface every state with
 * dedicated long-form coverage.
 *
 * Returns: [{ stateCode, guides: [...] }, ...] sorted by state code,
 *   so the rendered grid reads alphabetically (CA, FL, IL, NY, TX).
 *   Multi-state and applicableEverywhere guides are intentionally
 *   excluded — they're not state-specific deep-dives.
 */
export function getGuidesGroupedByState() {
  const buckets = {};
  for (const g of GUIDES) {
    if (g.applicableEverywhere === true) continue;
    if (!g.relatedStates || g.relatedStates.length !== 1) continue;
    const code = g.relatedStates[0];
    (buckets[code] = buckets[code] || []).push(g);
  }
  for (const code of Object.keys(buckets)) {
    // Newest first within each state (matches sort used on calc pages).
    buckets[code].sort((a, b) => {
      if (a.date && b.date && a.date !== b.date) return a.date < b.date ? 1 : -1;
      return a.title.localeCompare(b.title);
    });
  }
  return Object.keys(buckets)
    .sort()
    .map((stateCode) => ({ stateCode, guides: buckets[stateCode] }));
}
